// Segmented bar chart of the four aging buckets (task 5.1 of the
// dashboard-redesign spec — Req 5.3).
//
// A read-only Wave-4 widget that visualises the tenant's outstanding
// arrears distribution across `0–30`, `31–60`, `61–90`, and `90+` days
// past due. The numbers come from the same `reports` aged-debtors
// endpoint the KPI tile uses (Constitution Rule 2 — the GL Debtors
// Control account is the source of truth; this widget does NOT
// re-aggregate financial records).
//
// State model (see design.md §Widget state contracts):
//   - loading → <WidgetSkeleton>       (bounding box stable)
//   - empty   → "No arrears aging"     (when totalPence is 0)
//   - ready   → 4 horizontal bars sized proportionally to bucket value,
//               with severity colouring (green→amber→orange→red)
//   - error   → <WidgetError> with ErrorId + Retry (Req 14)
//
// The entire subtree is wrapped in <ErrorBoundary> so a render exception
// inside this widget cannot blank the surrounding dashboard (P4 in
// design.md §Correctness Properties).

import React from "react";
import { Link } from "react-router-dom";
import { ErrorBoundary } from "../../../components/ui/ErrorBoundary";
import { WidgetFrame } from "../WidgetFrame";
import { WidgetError } from "../WidgetError";
import { useDashboardArrears } from "../hooks/useDashboardArrears";
import { formatMoneyGBP } from "../formatMoney";
import type { ArrearsAging, BuildingScope } from "../types";

/** Widget props — every dashboard widget takes exactly `scope`. */
export interface WidgetArrearsAgingProps {
  scope: BuildingScope;
}

/** Human-readable widget name — surfaced in the scoped error card title. */
const WIDGET_NAME = "Arrears Aging";

/** Empty-state copy per task 5.1 brief. */
const EMPTY_COPY = "No arrears aging";

/**
 * Minimum bar width, as a percentage, applied to any bucket whose value is
 * strictly greater than zero. Ensures a small but non-zero bar remains
 * visible on the chart rather than collapsing to an invisible sliver.
 */
const MIN_NON_ZERO_BAR_WIDTH_PERCENT = 2;

/**
 * Severity colouring for the four aging buckets. Ordered oldest-newest so
 * the visual progression reads left-to-right in the chart and matches the
 * order of `ArrearsAging.buckets` keys.
 *
 * - `0–30`  green  `#16a34a` — recently overdue, still recoverable.
 * - `31–60` amber  `#f59e0b` — chase now, before it escalates.
 * - `61–90` orange `#fb923c` — approaching legal / debt-recovery action.
 * - `90+`   red    `#dc2626` — write-off risk, escalation required.
 */
const BUCKETS: ReadonlyArray<{
  key: keyof ArrearsAging["buckets"];
  label: string;
  colour: string;
}> = [
  { key: "days0to30", label: "0–30", colour: "#16a34a" },
  { key: "days31to60", label: "31–60", colour: "#f59e0b" },
  { key: "days61to90", label: "61–90", colour: "#fb923c" },
  { key: "days90plus", label: "90+", colour: "#dc2626" },
];

/**
 * Build the deep-link `to` prop for `<Link>`. Aggregate mode links to the
 * report unfiltered; single-building mode carries the `?buildingId=` query
 * so the report page pre-filters to the same building.
 */
function buildAgedDebtorsHref(scope: BuildingScope): string {
  if (scope.kind === "single") {
    return `/dashboard/reports/aged-debtors?buildingId=${encodeURIComponent(
      scope.buildingId,
    )}`;
  }
  return "/dashboard/reports/aged-debtors";
}

/**
 * Compute the width percentage for a bar given its pence value and the
 * total across all buckets. Values > 0 are clamped to at least
 * {@link MIN_NON_ZERO_BAR_WIDTH_PERCENT} so a small non-zero bar stays
 * visible; zero always renders as 0% (no visible bar) so the chart doesn't
 * imply a value where there is none.
 */
function computeBarWidthPercent(
  bucketPence: number,
  totalPence: number,
): number {
  if (bucketPence <= 0 || totalPence <= 0) return 0;
  const raw = (bucketPence / totalPence) * 100;
  return Math.max(raw, MIN_NON_ZERO_BAR_WIDTH_PERCENT);
}

/**
 * Widget_ArrearsAging — a segmented bar chart of the four aging buckets
 * from `ArrearsAging.buckets`, with a deep-link to the aged debtors report.
 *
 * @remarks
 * Wraps in `<ErrorBoundary>` so a render exception here cannot blank the
 * surrounding dashboard. The four render states are routed by
 * `<WidgetFrame>`; this component only owns the ready-state layout.
 */
export function Widget_ArrearsAging({
  scope,
}: WidgetArrearsAgingProps): JSX.Element {
  return (
    <ErrorBoundary
      fallback={
        <WidgetError
          errorId="widget-arrears-aging-render-error"
          name={WIDGET_NAME}
        />
      }
    >
      <WidgetArrearsAgingInner scope={scope} />
    </ErrorBoundary>
  );
}

function WidgetArrearsAgingInner({
  scope,
}: WidgetArrearsAgingProps): JSX.Element {
  const result = useDashboardArrears(scope);
  const href = buildAgedDebtorsHref(scope);

  return (
    <WidgetFrame
      result={result}
      name={WIDGET_NAME}
      emptyCopy={EMPTY_COPY}
      skeletonHeight={200}
    >
      {result.status === "ready" && (
        <div
          data-testid="widget-arrears-aging"
          className="w-full h-full flex flex-col gap-2 px-4 py-3"
        >
          <div
            className="text-xs uppercase tracking-wide"
            style={{ color: "#6b7280", fontWeight: 500 }}
          >
            {WIDGET_NAME}
          </div>

          <ul
            data-testid="widget-arrears-aging-bars"
            className="flex flex-col gap-2 w-full"
            aria-label="Arrears aging distribution"
          >
            {BUCKETS.map(({ key, label, colour }) => {
              const pence = result.data.buckets[key];
              const widthPercent = computeBarWidthPercent(
                pence,
                result.data.totalPence,
              );
              return (
                <li
                  key={key}
                  data-testid={`widget-arrears-aging-bar-${key}`}
                  className="flex items-center gap-2 text-xs"
                >
                  <span
                    className="inline-block font-medium text-right"
                    style={{ color: "#374151", width: 44 }}
                  >
                    {label}
                  </span>
                  <span
                    className="relative flex-1 rounded-full overflow-hidden"
                    style={{ backgroundColor: "#f3f4f6", height: 10 }}
                    aria-hidden="true"
                  >
                    <span
                      data-testid={`widget-arrears-aging-bar-fill-${key}`}
                      className="block h-full rounded-full"
                      style={{
                        backgroundColor: colour,
                        width: `${widthPercent}%`,
                      }}
                    />
                  </span>
                  <span
                    data-testid={`widget-arrears-aging-amount-${key}`}
                    className="tabular-nums text-right"
                    style={{ color: colour, minWidth: 72, fontWeight: 500 }}
                  >
                    {formatMoneyGBP(pence)}
                  </span>
                </li>
              );
            })}
          </ul>

          <Link
            to={href}
            data-testid="widget-arrears-aging-link"
            className="text-xs hover:underline self-start"
            style={{ color: "#2563eb" }}
          >
            View aged debtors →
          </Link>
        </div>
      )}
    </WidgetFrame>
  );
}

export default Widget_ArrearsAging;
